import {
  Component,
  Host,
  Prop,
  Element,
  State,
  h,
  Watch,
  Event,
  EventEmitter
} from "@stencil/core";

import { MercuryBundles } from "@genexus/mercury";
import { TabModel } from "@genexus/chameleon-controls-library";
import { Locale } from "../../../common/locale";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/tab",
  "utils/form--full",
  "utils/layout",
  "utils/typography",
  "utils/spacing",
  "chameleon/scrollbar"
];

const MAIN_SECTION_CLASSES: string = "main";
const WEB_APPS_TAB_SELECTOR: string = "web-apps";
const NATIVE_APPS_TAB_SELECTOR: string = "native-apps";
const APIS_TAB_SELECTOR: string = "apis";

// Temporary WA (Read WA in launchpad.scss)
const TAB_SLOT_SPACING_BODY_SELECTOR: string = "tab-slot-spacing-body";

@Component({
  tag: "gx-ide-launchpad",
  styleUrl: "launchpad.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/launchpad"]
})
export class GxIdeLaunchpad {
  #componentLocale: any;

  @Element() el: HTMLGxIdeLaunchpadElement;

  /**
   * JSON data containing section structure
   */
  @Prop() data: string | undefined = undefined;

  /**
   * Determines if the component runs in standalone mode
   */
  @Prop() isStandalone: boolean = true;

  /**
   * Array of sections for tabs
   */
  @State() sections: Array<any> = [];

  /**
   * Tab model for ch-tab-render
   */
  @State() tabModel: TabModel = [];

  /**
   * Event emitted when an object is selected for execution
   */
  @Event() objectSelected: EventEmitter<{
    key: { type: string; id: string } | string;
  }>;

  /**
   * Event emitted when copying to clipboard
   */
  @Event() copyToClipboard: EventEmitter<{ url: string }>;

  /**
   * Event emitted when opening app store
   */
  @Event() openOnTheAppStore: EventEmitter<{ url: string }>;

  @Watch("data")
  watchDataHandler(newValue: string | undefined) {
    if (newValue !== undefined) {
      this.organizeData();
    }
  }

  async componentWillLoad() {
    try {
      this.#componentLocale = await Locale.getComponentStrings(this.el);
    } catch (error) {
      console.error("Error loading locale:", error);
      this.#componentLocale = {
        tabs: {
          webApps: "WEB APPS",
          nativeApps: "NATIVE APPS",
          apis: "APIS"
        }
      };
    }

    if (this.data) {
      this.organizeData();
    }
  }

  componentDidLoad() {}

  /**
   * Organizes received data and builds tab model
   */
  private organizeData(): void {
    if (this.data) {
      try {
        const parsedData =
          typeof this.data === "string" ? JSON.parse(this.data) : this.data;
        this.sections = parsedData.sections || [];
        this.buildTabModel();
      } catch (error) {
        console.error("Error parsing launchpad data:", error);
        this.sections = [];
        this.tabModel = [];
      }
    }
  }

  /**
   * Builds tab model based on sections
   */
  private buildTabModel(): void {
    if (this.sections && this.sections.length > 0) {
      this.tabModel = this.sections.map(section => ({
        id: section.id,
        name: this.getLocalizedTabName(section.id)
      }));
    }
  }

  /**
   * Gets localized tab name based on section ID
   */
  private getLocalizedTabName(sectionId: string): string {
    const tabNames = this.#componentLocale?.tabs || {};

    switch (sectionId) {
      case "web-apps":
        return tabNames.webApps || "WEB APPS";
      case "native-apps":
        return tabNames.nativeApps || "NATIVE APPS";
      case "apis":
        return tabNames.apis || "APIS";
      default:
        return sectionId.toUpperCase().replace(/-/g, " ");
    }
  }

  /**
   * Handles object selection from subcomponents
   */
  private handleObjectSelection(event: any) {
    let key = event.detail.key;

    // Convert string format "type|id" to object format
    if (typeof key === "string" && key.includes("|")) {
      const parts = key.split("|");
      if (parts.length === 2) {
        key = {
          type: parts[0],
          id: parts[1]
        };
      }
    }

    this.objectSelected.emit({ key });
  }

  /**
   * Handles copy to clipboard from subcomponents
   */
  private handleCopyToClipboard(event: any) {
    this.copyToClipboard.emit({ url: event.detail.url });
  }

  /**
   * Handles open app store from subcomponents
   */
  private handleOpenAppStore(event: any) {
    this.openOnTheAppStore.emit({ url: event.detail.url });
  }

  render() {
    if (!this.data || !this.sections || this.sections.length === 0) {
      return null;
    }

    // TODO: Revisit re-enabling keyboard shortcuts 1, 2, 3 for switching launchpad tabs (Web Apps, Native Apps, APIs)
    return (
      <Host>
        <ch-theme model={CSS_BUNDLES}></ch-theme>

        <ch-tab-render
          class={{
            scrollable: true,
            tab: true,
            [MAIN_SECTION_CLASSES]: true
          }}
          tabListPosition="block-start"
          model={this.tabModel}
          selectedId={WEB_APPS_TAB_SELECTOR}
          overflow="auto"
          contain="size"
        >
          <div
            slot={WEB_APPS_TAB_SELECTOR}
            class={{ "web-apps": true, [TAB_SLOT_SPACING_BODY_SELECTOR]: true }}
            part="tab-button-web-apps"
          >
            <gx-ide-launchpad-web-apps
              objects={
                this.sections.find(s => s.id === "web-apps")?.objects || []
              }
              {...({
                onObjectSelected: (event: any) =>
                  this.handleObjectSelection(event)
              } as any)}
              {...({
                onCopyToClipboard: (event: any) =>
                  this.handleCopyToClipboard(event)
              } as any)}
            />
          </div>

          <div
            slot={NATIVE_APPS_TAB_SELECTOR}
            class={{
              "native-apps": true,
              [TAB_SLOT_SPACING_BODY_SELECTOR]: true
            }}
            part="tab-button-native-apps"
          >
            <gx-ide-launchpad-native-apps
              objects={
                this.sections.find(s => s.id === "native-apps")?.objects || []
              }
              {...({
                onObjectSelected: (event: any) =>
                  this.handleObjectSelection(event)
              } as any)}
              {...({
                onCopyToClipboard: (event: any) =>
                  this.handleCopyToClipboard(event)
              } as any)}
              {...({
                onOpenOnTheAppStore: (event: any) =>
                  this.handleOpenAppStore(event)
              } as any)}
            />
          </div>

          <div
            slot={APIS_TAB_SELECTOR}
            class={{ apis: true, [TAB_SLOT_SPACING_BODY_SELECTOR]: true }}
            part="tab-button-apis"
          >
            <gx-ide-launchpad-apis
              apis={this.sections.find(s => s.id === "apis")?.apis || []}
              {...({
                onObjectSelected: (event: any) =>
                  this.handleObjectSelection(event)
              } as any)}
              {...({
                onCopyToClipboard: (event: any) =>
                  this.handleCopyToClipboard(event)
              } as any)}
            />
          </div>
        </ch-tab-render>
      </Host>
    );
  }
}
